/**
 * useEditModeLongPress — detects a ~500ms hold anywhere on a tile
 * and calls onEditModeEnter to transition into edit mode.
 *
 * Spec: ios-fluidity-spec §2.1 (D-016).
 *   - Touch, mouse, and pen: 500ms hold.
 *   - Cancel if pointer moves > 8px from the initial position (slop guard).
 *   - Reduced-motion: functionality identical (we still enter edit mode,
 *     just no haptic), motion-free.
 *   - Must NOT fire when already in edit mode (onEditModeEnter is a no-op
 *     guard if isEditMode=true, but we also skip attaching the timer).
 *
 * Returns { onPointerDown, onPointerUp, onPointerMove, onPointerCancel }
 * to attach to the tile wrapper.
 *
 * This is separate from the drag-start 300ms delay (use-long-press.ts) which
 * operates inside edit mode after it is already active.
 *
 * Immutability: no state mutations; timer ID managed via ref.
 */

export const EDIT_MODE_LONG_PRESS_MS = 500;
const SLOP_PX = 8;

export interface EditModeLongPressHandlers {
  readonly onPointerDown: (e: React.PointerEvent) => void;
  readonly onPointerUp: () => void;
  readonly onPointerMove: (e: React.PointerEvent) => void;
  readonly onPointerCancel: () => void;
}

import { useRef, useCallback } from 'react';

/**
 * Hook that returns pointer handlers to attach to a tile wrapper.
 * When the user holds the tile for ≥500ms without moving >8px, calls
 * onEditModeEnter and optionally fires a haptic pulse.
 *
 * @param isEditMode - current edit mode state; if true, handlers are no-ops.
 * @param onEditModeEnter - callback to enter edit mode (typically toggleEditMode).
 */
export function useEditModeLongPress(
  isEditMode: boolean,
  onEditModeEnter: () => void,
): EditModeLongPressHandlers {
  const timerRef = useRef<number>(-1);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);
  // Stores the element that received pointerdown so we can suppress the
  // synthetic click that browsers fire after a long-press (FIX C1).
  const elementRef = useRef<HTMLElement | null>(null);

  const cancel = useCallback(() => {
    if (timerRef.current !== -1) {
      clearTimeout(timerRef.current);
      timerRef.current = -1;
    }
    startPosRef.current = null;
    // Clear the pending element ref — suppressor must not be installed when
    // the press is cancelled before the timer fires.
    elementRef.current = null;
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Already in edit mode — skip (drag-start timer takes over).
      if (isEditMode) return;
      // Only primary button for mouse (button=0); touch/pen have button=0 always.
      if (e.button !== 0) return;

      cancel();
      startPosRef.current = { x: e.clientX, y: e.clientY };
      // Capture the target element so the timer callback can install a click
      // suppressor on the exact element that will emit the synthetic click.
      elementRef.current = e.currentTarget as HTMLElement;

      timerRef.current = window.setTimeout(() => {
        timerRef.current = -1;
        startPosRef.current = null;

        const target = elementRef.current;
        elementRef.current = null;

        // Install a capture-phase, once click suppressor BEFORE calling
        // onEditModeEnter so the detail sheet cannot open on top of edit mode
        // when the browser fires a synthetic click after the long-press (FIX C1).
        if (target) {
          target.addEventListener(
            'click',
            (evt: MouseEvent) => {
              evt.stopPropagation();
              evt.preventDefault();
            },
            { capture: true, once: true },
          );
        }

        // Haptic hint if available (reduced-motion: vibrate still allowed, it's not visual).
        if (typeof navigator !== 'undefined' && navigator.vibrate) {
          navigator.vibrate(10);
        }
        onEditModeEnter();
      }, EDIT_MODE_LONG_PRESS_MS);
    },
    [isEditMode, onEditModeEnter, cancel],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (timerRef.current === -1 || !startPosRef.current) return;
      const dx = e.clientX - startPosRef.current.x;
      const dy = e.clientY - startPosRef.current.y;
      if (Math.sqrt(dx * dx + dy * dy) > SLOP_PX) {
        cancel();
      }
    },
    [cancel],
  );

  const onPointerUp = useCallback(() => {
    cancel();
  }, [cancel]);

  const onPointerCancel = useCallback(() => {
    cancel();
  }, [cancel]);

  return { onPointerDown, onPointerUp, onPointerMove, onPointerCancel };
}
